export type IssueStatus = 'open' | 'investigating' | 'resolved';
export type IssueSeverity = 'critical' | 'high' | 'medium';

/** The source a flagged turn came from — a governance policy verdict, or a sensitivity-flag hit. */
export type IssueKind = 'policy' | 'sensitivity';

/**
 * A governance issue = ONE flagged conversation turn, from one of two sources:
 * - `policy`: a turn a classification policy gave a concerning verdict (ref = `${policyId}:${interactionId}`).
 * - `sensitivity`: a turn carrying a sensitivity flag — PII, credentials, data exposure (ref = `${flag}:${interactionId}`).
 *
 * Triage state (status, flag, assignee, resolution, notes) is layered on by
 * {@link GovernanceIssueStore}, keyed by {@link key}. The turn's conversation is shown in
 * the detail drawer via `sessionId`/`turnIndex`.
 */
export interface GovernanceIssue {
    kind: IssueKind;
    /** `${policyId}:${interactionId}` (policy) or `${flag}:${interactionId}` (sensitivity). */
    ref: string;
    /** Stable id for triage state: `${kind}:${ref}`. */
    key: string;
    /** The flagged turn. */
    interactionId: string;
    sessionId: string;
    turnIndex: number;
    /** Row title — policy name, or humanized flag label. */
    title: string;
    /** The concerning verdict / flag, formatted for display. */
    verdict: string;
    severity: IssueSeverity;
    person: string;
    department: string;
    timestampIso: string;
    confidence: number | null;
}

const STATUS_ORDER: Record<IssueStatus, number> = { open: 0, investigating: 1, resolved: 2 };
const SEVERITY_RANK: Record<IssueSeverity, number> = { critical: 3, high: 2, medium: 1 };

/** Per-flag severity for a single sensitivity-flagged turn (volume no longer applies at turn grain). */
const FLAG_SEVERITY: Record<string, IssueSeverity> = {
    credentials: 'critical',
    pii: 'high',
    data_exposure: 'high',
};

export function flagTurnSeverity(flag: string): IssueSeverity {
    return FLAG_SEVERITY[flag] ?? 'medium';
}

/** Compose the stable triage key for an issue. */
export function issueKey(kind: IssueKind, ref: string): string {
    return `${kind}:${ref}`;
}

/** Compose the per-turn ref for an issue source (`${sourceId}:${interactionId}`). */
export function issueRef(sourceId: string, interactionId: string): string {
    return `${sourceId}:${interactionId}`;
}

/** Sort: Open first; then by severity (critical → medium); then newest turn first. */
export function compareIssues(a: GovernanceIssue & { status: IssueStatus }, b: GovernanceIssue & { status: IssueStatus }): number {
    return (
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] ||
        b.timestampIso.localeCompare(a.timestampIso)
    );
}
